import { DOCUMENT, inject, RendererFactory2, Service } from '@angular/core';

declare global {
  interface Window {
    // eslint-disable-next-line custom-rules/prefer-interface-property-readonly
    dataLayer: unknown[];
  }
}

export interface GoogleAnalyticsConfig {
  readonly defaultId: string;
  readonly ids: Record<string, {
    readonly containerId: string;
    readonly measurementId: string;
  }>;
}

export type GoogleAnalyticsProperties = Record<string, string>;

@Service()
export class GoogleAnalyticsService {
  readonly #document = inject(DOCUMENT);
  readonly #loadedContainerIds: string[] = [];
  readonly #renderer2 = inject(RendererFactory2).createRenderer(null, null);

  #config?: GoogleAnalyticsConfig;

  activate(id: string): void {
    const entry = this.#config?.ids[id] ?? this.#config?.ids[this.#config?.defaultId];

    if (!entry) {
      return;
    }

    if (!this.#loadedContainerIds.includes(entry.containerId)) {
      this.#loadedContainerIds.push(entry.containerId);
      this.#injectGtmScript(entry.containerId);
    }

    const win = window as unknown as Record<string, boolean>;

    Object.values(this.#config!.ids).forEach(({ measurementId }) => {
      win[`ga-disable-${measurementId}`] = measurementId !== entry.measurementId;
    });
  }

  activateDefault(): void {
    if (!this.#config) {
      return;
    }

    this.activate(this.#config.defaultId);
  }

  addEvent(event: string): void {
    if (!this.#config) {
      return;
    }

    window.dataLayer.push({ event });
  }

  addProperties(props: GoogleAnalyticsProperties): void {
    if (!this.#config) {
      return;
    }

    window.dataLayer.push(props);
    this.addEvent('properties_updated');
  }

  configure(config: GoogleAnalyticsConfig): void {
    this.#config = config;
  }

  identify(userId: string): void {
    if (!this.#config) {
      return;
    }

    if (!userId) {
      console.warn('GoogleAnalyticsService.identify: userId is not defined');

      return;
    }

    window.dataLayer.push({
      // eslint-disable-next-line camelcase
      user_id: userId,
    });
  }

  removeProperties(props: string[]): void {
    if (!this.#config) {
      return;
    }

    const properties: GoogleAnalyticsProperties = {};

    props.forEach((prop) => {
      properties[prop] = 'not_passed';
    });

    window.dataLayer.push(properties);
    this.addEvent('properties_updated');
  }

  #injectGtmScript(containerId: string): void {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': Date.now(),
      event: 'gtm.js',
    });

    const script = this.#renderer2.createElement('script') as HTMLScriptElement;
    const scriptSrc = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;

    this.#renderer2.setAttribute(script, 'async', '');
    this.#renderer2.setAttribute(script, 'src', scriptSrc);
    this.#renderer2.appendChild(this.#document.head, script);
  }
}
